'use client'

import { useCallback } from 'react'
import { useRouter } from 'next/navigation'
import { useClientSelection } from '@/providers/ClientSelectionContext'
import { useClientPortalContext } from '@/providers/ClientPortalContext'
import { trackEvent } from '@/utilities/analytics/trackEvent'
import {
  buildOrgDashboardUrl,
  buildProgramContextDashboardUrl,
} from '@/utilities/dashboard/orgProgramContextUrl'
import type {
  SwitcherOrganization,
  SwitcherSelection,
} from '@/utilities/dashboard/orgProgramSwitcherTree'
import type { CachedPartner } from '@/utilities/partnersCache'

export type OrgProgramSwitcherController = {
  /** True once partner flags + role access for the org-dashboard flag have settled. */
  isAccessReady: boolean
  isFlagEnabled: boolean
  /** Nested switcher should render (flag on and orgs available). */
  showNestedSwitcher: boolean
  /** Flat ClientList should render (flag off, org load failure, or empty nested tree). */
  showFlatSwitcher: boolean
  /** Dashboard URL for the currently selected org/program context. */
  dashboardHref: string
  organizations: SwitcherOrganization[]
  partners: CachedPartner[]
  partnersLoading: boolean
  selection: SwitcherSelection | null
  capabilityPartnerId: string | null
  isOrgContext: boolean
  isProgramScopedNavDisabled: boolean
  loading: boolean
  onSelectOrg: (organization: SwitcherOrganization) => void
  onSelectProgram: (organization: SwitcherOrganization, programId: string) => void
  onSelectFlatProgram: (programId: string, programName: string) => void
}

/**
 * Flag-gated org/program switcher orchestration for the Client Portal header.
 * Keeps NavbarTop as layout composition only.
 */
export function useOrgProgramSwitcher(): OrgProgramSwitcherController {
  const router = useRouter()
  const { selectedClientId, setSelectedClientId } = useClientSelection()
  const {
    access,
    organizations,
    partners,
    partnersLoading,
    selection,
    capabilityPartnerId,
    dashboardHref,
    isDashboardRoute,
    isOrgContext,
    isProgramScopedNavDisabled,
    showNestedSwitcher,
    showFlatSwitcher,
    switcherLoading,
  } = useClientPortalContext()

  const onSelectOrg = useCallback(
    (organization: SwitcherOrganization) => {
      if (!organization.primaryPartnerId) return
      trackEvent('client:select_partner:option_click', {
        source: 'navbar',
        option: organization.name,
        context_mode: 'org',
      })
      router.push(buildOrgDashboardUrl(organization.primaryPartnerId))
    },
    [router],
  )

  const selectProgram = useCallback(
    (programId: string, programName: string, navigateToDashboard: boolean) => {
      trackEvent('client:select_partner:option_click', {
        source: 'navbar',
        option: programName,
        context_mode: 'program',
      })
      if (!navigateToDashboard && selectedClientId !== programId) {
        setSelectedClientId(programId)
      }
      if (navigateToDashboard) {
        router.push(buildProgramContextDashboardUrl(programId))
      }
    },
    [router, selectedClientId, setSelectedClientId],
  )

  const onSelectProgram = useCallback(
    (organization: SwitcherOrganization, programId: string) => {
      const program = organization.programs.find((candidate) => candidate.id === programId)
      selectProgram(programId, program?.name ?? programId, true)
    },
    [selectProgram],
  )

  const onSelectFlatProgram = useCallback(
    (programId: string, programName: string) => {
      const navigateToDashboard = isDashboardRoute
      if (selectedClientId === programId && !navigateToDashboard) return
      selectProgram(programId, programName, navigateToDashboard)
    },
    [isDashboardRoute, selectProgram, selectedClientId],
  )

  return {
    isAccessReady: access.isReady,
    isFlagEnabled: access.isFlagEnabled,
    showNestedSwitcher,
    showFlatSwitcher,
    dashboardHref,
    organizations,
    partners,
    partnersLoading,
    selection,
    capabilityPartnerId,
    isOrgContext,
    isProgramScopedNavDisabled,
    loading: switcherLoading,
    onSelectOrg,
    onSelectProgram,
    onSelectFlatProgram,
  }
}
